"use client";

import React, { useMemo } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { formatPrice, cn } from "@/lib/utils";
import type { PriceSuggestion } from "@/types/price-insight";
import type { SellPriceBandSnapshot } from "@/lib/hooks/useSellPriceBand";
import { classifyEnforcedBandPrice } from "@/lib/pricing/sellerPriceBandCheck";
import {
  computeBandRecommendationPrices,
  getOrderedBandPriceChips,
  type BandPriceChipTier,
} from "@/lib/pricing/sellerPriceBandRecommendations";
import { BAND_PRICE_CHIP_STYLES } from "@/lib/pricing/bandPriceChipStyles";
import {
  getSellerBandAboveRangeMessage,
  getSellerBandAboveRangeSecondaryHint,
  getSellerBandBelowRangeMessage,
  getSellerBandChipRecommendedBadge,
  getSellerBandChipTierLabel,
  getSellerBandChipTierSubtitle,
  getSellerBandChipsEnforcementHeading,
  getSellerBandChipsEnforcementIntro,
  getSellerBandChipsFooterHint,
  getSellerBandEnforcementHeading,
  getSellerBandEnforcementIntro,
  getSellerBandInRangeMessage,
  getSellerBandRecommendationChipsHeading,
  getSellerBandTypicalRangeLabelForBand,
  getSellerBandUnavailableMessage,
  getSellerBandUseRecommendedLabel,
  resolveSellerBandPolicyHint,
} from "@/lib/pricingInsightCopy";

export type SellerBandEnforcementPreviewProps = {
  itemTitle: string;
  currentPrice?: number;
  band: SellPriceBandSnapshot | null;
  bandError?: string | null;
  bandRecommendationChipsEnabled: boolean;
  priceSuggestion?: PriceSuggestion | null;
  /** When set, chips are clickable (sell flow). Omit for admin read-only preview. */
  onChipSelect?: (price: number, tier: BandPriceChipTier) => void;
  /** Admin read-only: highlight chip matching listing price. */
  highlightListingPrice?: boolean;
  className?: string;
};

/**
 * Seller-facing band enforcement UI — shared by /sell and admin "What the seller sees".
 */
export default function SellerBandEnforcementPreview({
  itemTitle,
  currentPrice,
  band,
  bandError = null,
  bandRecommendationChipsEnabled,
  priceSuggestion = null,
  onChipSelect,
  highlightListingPrice = false,
  className,
}: SellerBandEnforcementPreviewProps) {
  const policyHint = resolveSellerBandPolicyHint({
    bandPolicy: band?.bandPolicy,
    cohortTier: band?.cohortTier,
    enforcementEligible: band?.enforcementEligible,
  });
  const isEnforced = Boolean(band?.enforcementEligible);
  const rangeLabel = getSellerBandTypicalRangeLabelForBand(band);
  const bandStatus =
    band && isEnforced && currentPrice != null
      ? classifyEnforcedBandPrice(currentPrice, band)
      : null;

  const showChips =
    bandRecommendationChipsEnabled &&
    Boolean(band?.enforcementEligible && band.min > 0 && band.max > 0);
  const chipPrices =
    showChips && band ? computeBandRecommendationPrices(band) : null;
  const orderedChips =
    chipPrices != null ? getOrderedBandPriceChips(chipPrices) : null;

  const recommendedChip = useMemo(() => {
    if (!orderedChips?.length) return null;
    return orderedChips.find((chip) => chip.isRecommended) ?? null;
  }, [orderedChips]);

  const listingMatchesChipTier = useMemo(() => {
    if (
      !highlightListingPrice ||
      currentPrice == null ||
      !orderedChips?.length
    ) {
      return null;
    }
    const match = orderedChips.find((chip) => chip.price === currentPrice);
    return match?.tier ?? null;
  }, [currentPrice, highlightListingPrice, orderedChips]);

  const handleUseRecommended = () => {
    if (!onChipSelect || !recommendedChip) return;
    onChipSelect(recommendedChip.price, recommendedChip.tier);
  };

  return (
    <div className={cn("space-y-3", className)}>
      <div>
        <div className="flex items-center gap-2">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 shrink-0" />
          <h3 className="text-sm font-semibold text-foreground">
            {showChips
              ? getSellerBandChipsEnforcementHeading()
              : getSellerBandEnforcementHeading()}
          </h3>
        </div>
        <p className="mt-1 text-sm text-text-light">
          {isEnforced
            ? showChips
              ? getSellerBandChipsEnforcementIntro(itemTitle)
              : getSellerBandEnforcementIntro(itemTitle)
            : null}
        </p>
      </div>

      {bandError && !band ? (
        <p className="text-sm text-amber-800">
          {getSellerBandUnavailableMessage()}
        </p>
      ) : null}

      {showChips && orderedChips ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            {getSellerBandRecommendationChipsHeading()}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {orderedChips.map((chip) => {
              const styles = BAND_PRICE_CHIP_STYLES[chip.tier];
              const isListingPrice =
                highlightListingPrice &&
                currentPrice != null &&
                currentPrice === chip.price;
              const isListingTier = listingMatchesChipTier === chip.tier;
              const chipClassName = `w-full rounded-lg border bg-surface px-3 py-2.5 text-left text-sm ${
                onChipSelect
                  ? `transition focus:outline-none focus:ring-2 focus:ring-offset-1 ${styles.button}`
                  : styles.button
              } ${
                chip.isRecommended || isListingPrice || isListingTier
                  ? `ring-2 ${styles.recommendedRing}`
                  : ""
              }`;

              const chipBody = (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-foreground">
                      {getSellerBandChipTierLabel(chip.tier)}
                    </span>
                    {chip.isRecommended ? (
                      <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                        {getSellerBandChipRecommendedBadge()}
                      </span>
                    ) : null}
                  </div>
                  <span
                    className={`mt-0.5 block text-base font-semibold ${styles.price}`}
                  >
                    {formatPrice(chip.price)}
                  </span>
                  <span className="mt-1 block text-xs text-text-light">
                    {getSellerBandChipTierSubtitle(chip.tier)}
                  </span>
                  {isListingPrice ? (
                    <span className="mt-1 block text-xs font-medium text-foreground">
                      Listing price
                    </span>
                  ) : null}
                </>
              );

              if (onChipSelect) {
                return (
                  <button
                    key={chip.tier}
                    type="button"
                    onClick={() => onChipSelect(chip.price, chip.tier)}
                    className={chipClassName}
                  >
                    {chipBody}
                  </button>
                );
              }

              return (
                <div key={chip.tier} className={chipClassName}>
                  {chipBody}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-text-light">
            {getSellerBandChipsFooterHint()}
          </p>
        </div>
      ) : null}

      {bandStatus === "above_band" && band ? (
        <div className="space-y-2 rounded-md bg-surface border border-red-200 p-3">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden />
            <span>{getSellerBandAboveRangeMessage()}</span>
          </div>
          {onChipSelect && recommendedChip && showChips ? (
            <>
              <button
                type="button"
                onClick={handleUseRecommended}
                className="w-full rounded-lg border border-sky-300 bg-sky-50 px-3 py-2.5 text-sm font-medium text-sky-900 transition hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1"
              >
                {getSellerBandUseRecommendedLabel(recommendedChip.price)}
              </button>
              <p className="text-xs text-text-light">
                {getSellerBandAboveRangeSecondaryHint()}
              </p>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Advisory / non-chip enforce: show range only when not fishing for a ceiling (advisory). */}
      {!showChips && band && band.min > 0 && band.max > 0 && !isEnforced ? (
        <div className="rounded-md bg-surface border border-border p-3 space-y-2">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-text-light">{rangeLabel}</span>
            <span className="text-sm font-semibold text-foreground">
              {formatPrice(band.min)} – {formatPrice(band.max)}
            </span>
          </div>
        </div>
      ) : null}

      {!showChips &&
      band &&
      band.min > 0 &&
      band.max > 0 &&
      isEnforced &&
      bandStatus &&
      bandStatus !== "above_band" ? (
        <div className="rounded-md bg-surface border border-border p-3 space-y-2">
          <div
            className={`flex items-start gap-2 text-sm ${
              bandStatus === "in_band" ? "text-primary-700" : "text-amber-800"
            }`}
          >
            {bandStatus === "in_band" ? (
              <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden />
            ) : (
              <ExclamationTriangleIcon
                className="h-5 w-5 shrink-0"
                aria-hidden
              />
            )}
            <span>
              {bandStatus === "in_band"
                ? getSellerBandInRangeMessage()
                : getSellerBandBelowRangeMessage()}
            </span>
          </div>
        </div>
      ) : null}

      {policyHint ? (
        <p className="text-sm text-amber-800">{policyHint}</p>
      ) : null}

      {priceSuggestion && !band?.enforcementEligible ? (
        <div className="text-sm text-text-light border-t border-border pt-3">
          <p className="font-medium text-foreground mb-1">Suggested price</p>
          <p>{formatPrice(priceSuggestion.suggestedPrice)}</p>
        </div>
      ) : null}
    </div>
  );
}
